/*
 * UI shell for the shopper chat. Interpretation lives in chat-shopper.js;
 * this file only handles the panel, the transcript and the redirect.
 */
(function () {
  'use strict';

  var TEXT = {
    es: {
      thinking: 'Buscando...',
      understood: 'Busco: ',
      results: function (n) {
        return n === 1 ? 'Encontré 1 producto.' : 'Encontré ' + n + ' productos.';
      },
      capped: 'Encontré varios productos.',
      cta: 'Ver resultados',
      empty: 'No encontré lo qué buscas. Prueba mencionando el tipo de prenda, un color o la ocasión. Por ejemplo: leggings rosados para gimnasio.',
      nothing: function (what) {
        return what ? 'No tenemos ' + what + ' por ahora. Prueba con otro color o con otro tipo de prenda.'
                    : 'No tenemos nada así por ahora. Prueba con otro color o con otro tipo de prenda.';
      },
      // nouns, so several relaxed filters read as one sentence instead of a list
      relaxed: { ocasion: 'la ocasión', color: 'el color', silueta: 'el tipo de prenda', gender: 'para quién es' },
      relaxedPrefix: 'No encontré nada que cumpla también con ',
      relaxedSuffix: ', así que amplié la búsqueda.',
      lastJoiner: ' ni ',
      tidy: ', en ',
      tidyInto: ' en ',
      failed: 'No pude conectar con la búsqueda. Intenta de nuevo.',
      widened: 'No había nada solo para ese género, así que incluí también el otro.',
      collection: function (n) { return 'Esta es la colección ' + n + '.'; },
      collections: 'Encontré varias colecciones con ese nombre. ¿Cuál buscabas?',
      noCollection: 'No encontré una colección con ese nombre, así que busqué entre los productos.',
      inCollection: function (n) { return ' en la colección ' + n; },
      products: 'Encontré esto con ese nombre:',
      productsLoose: 'No encontré un producto con ese nombre, pero hay resultados parecidos.',
      whatsapp: 'Escríbenos por WhatsApp',
      whatsappHelp: '¿No encontrás lo que buscás? Escríbenos',
      // Prefilled so the shopper does not have to retype the request.
      whatsappIntro: function (q) { return 'Hola, estoy buscando: ' + q; },
      whatsappHi: 'Hola, necesito ayuda para encontrar un producto.'
    },
    en: {
      thinking: 'Searching...',
      understood: 'Looking for: ',
      results: function (n) {
        return n === 1 ? 'Found 1 product.' : 'Found ' + n + ' products.';
      },
      capped: 'Found plenty of products.',
      cta: 'See results',
      empty: 'No results found for your search. Try naming a garment, a colour or an occasion. For example: pink leggings for the gym.',
      nothing: function (what) {
        return what ? 'We have no ' + what + ' right now. Try another colour or another kind of garment.'
                    : 'We have nothing like that right now. Try another colour or another kind of garment.';
      },
      relaxed: { ocasion: 'the occasion', color: 'the colour', silueta: 'the kind of garment', gender: 'who it is for' },
      relaxedPrefix: 'Nothing also matched ',
      relaxedSuffix: ', so I widened the search.',
      lastJoiner: ' or ',
      tidy: ', in ',
      tidyInto: ' in ',
      failed: 'I could not reach the search. Please try again.',
      widened: 'There was nothing for that gender alone, so I included the other one as well.',
      collection: function (n) { return 'Here is the ' + n + ' collection.'; },
      collections: 'I found several collections with that name. Which one did you mean?',
      noCollection: 'I could not find a collection with that name, so I searched the products instead.',
      inCollection: function (n) { return ' in the ' + n + ' collection'; },
      products: 'Here is what I found with that name:',
      productsLoose: 'I could not find a product with that name, but there are similar results.',
      whatsapp: 'Message us on WhatsApp',
      whatsappHelp: 'Cannot find what you want? Message us',
      whatsappIntro: function (q) { return 'Hi, I am looking for: ' + q; },
      whatsappHi: 'Hi, I need help finding a product.'
    }
  };

  // Read straight from Shopify so the copy does not depend on script order.
  var COPY = TEXT[(window.Shopify && window.Shopify.locale) === 'en' ? 'en' : 'es'];

  // "the colour" / "the colour or the occasion" / "the colour, the occasion or who it is for"
  function joinNouns(list) {
    if (list.length === 1) return list[0];
    return list.slice(0, -1).join(', ') + COPY.lastJoiner + list[list.length - 1];
  }

  // A full page is 24 cards; more than that means the count is a floor.
  var PAGE_SIZE = 24;

  /*
   * The section is re-rendered on every page load, so the transcript only
   * survives a click on a result if it is stored. Per tab on purpose: the
   * conversation belongs to one visit, and stored result links go stale as
   * stock changes.
   */
  var STORE_KEY = 'chat-shopper:log';
  var MAX_TURNS = 40;

  // Storage throws outright in some privacy modes; never break the chat over it.
  function readStore() {
    try {
      var turns = JSON.parse(window.sessionStorage.getItem(STORE_KEY) || '[]');
      return Array.isArray(turns) ? turns : [];
    } catch (e) {
      return [];
    }
  }

  function writeStore(turns) {
    try {
      window.sessionStorage.setItem(STORE_KEY, JSON.stringify(turns));
    } catch (e) {
      /* quota or blocked storage: the chat still works, it just forgets */
    }
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text) n.textContent = text;
    return n;
  }

  class ChatShopper extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-chat-panel]');
      this.log = this.querySelector('[data-chat-log]');
      this.form = this.querySelector('[data-chat-form]');
      this.input = this.querySelector('[data-chat-input]');
      this.bubble = this.querySelector('[data-chat-open]');
      /*
       * Left empty on purpose when the section gives nothing: the engine then
       * builds the path from Shopify.routes.root, which carries the market
       * prefix. A hardcoded "/collections/all" would redirect and lose the
       * repeated filter params along the way.
       */
      this.base = this.dataset.base || '';
      this.whatsapp = (this.dataset.whatsapp || '').trim();
      // Blank falls back to COPY, which still follows the storefront locale.
      this.waText = (this.dataset.whatsappText || '').trim();
      /*
       * The icon ships as a <template> so the SVG stays in the theme snippet
       * that every other WhatsApp link on the site already uses.
       */
      var icon = this.querySelector('[data-chat-wa-icon]');
      this.waIcon = (icon && icon.content.firstElementChild) || null;
      if (this.whatsapp) this.renderFooter();
      this.turns = readStore();
      this.replay();

      this.bubble.addEventListener('click', this.toggle.bind(this));
      this.querySelector('[data-chat-close]').addEventListener('click', this.close.bind(this));
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          this.form.requestSubmit();
        }
      }.bind(this));
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !this.panel.hidden) this.close();
      }.bind(this));
    }

    toggle() {
      if (this.panel.hidden) this.open(); else this.close();
    }

    open() {
      this.panel.hidden = false;
      this.log.scrollTop = this.log.scrollHeight;
      this.bubble.setAttribute('aria-expanded', 'true');
      this.input.focus();
    }

    close() {
      this.panel.hidden = true;
      this.bubble.setAttribute('aria-expanded', 'false');
      this.bubble.focus();
    }

    /*
     * Null entries are skipped, so a caller can pass whatsappLink() straight
     * through without checking whether the section has a number configured.
     */
    render(text, who, links) {
      var msg = el('div', 'chat-shopper__msg chat-shopper__msg--' + (who || 'bot'), text);
      (links || []).filter(Boolean).forEach(function (link) {
        var a = el('a', link.className || 'chat-shopper__cta');
        a.href = link.href;
        if (link.external) {
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
        }
        if (link.icon && this.waIcon) a.appendChild(this.waIcon.cloneNode(true));
        a.appendChild(document.createTextNode(link.text));
        msg.appendChild(document.createElement('br'));
        msg.appendChild(a);
      }, this);
      return msg;
    }

    /*
     * Transient lines are shown but not stored: the "searching" placeholder is
     * dropped as soon as the reply lands, and a stored one would come back on
     * every page for the rest of the visit.
     */
    say(text, who, links, transient) {
      var msg = this.render(text, who, links);
      this.log.appendChild(msg);
      this.log.scrollTop = this.log.scrollHeight;
      if (!transient) this.remember(text, who, links);
      return msg;
    }

    remember(text, who, links) {
      this.turns.push({ t: text, w: who || 'bot', l: (links || []).filter(Boolean) });
      // Older turns are scrollback nobody reads; the tab should not grow forever.
      if (this.turns.length > MAX_TURNS) this.turns = this.turns.slice(-MAX_TURNS);
      writeStore(this.turns);
    }

    // Appended under the greeting, which stays the first line of the transcript.
    replay() {
      this.turns.forEach(function (turn) {
        this.log.appendChild(this.render(turn.t, turn.w, turn.l));
      }, this);
    }

    /*
     * Null when the section carries no number, which switches every WhatsApp
     * affordance off at once instead of needing a separate toggle.
     */
    whatsappLink(query) {
      if (!this.whatsapp) return null;
      var body = query ? COPY.whatsappIntro(query) : COPY.whatsappHi;
      var sep = this.whatsapp.indexOf('?') === -1 ? '?' : '&';
      return {
        // wa.me drops overly long prefills, so cap what the shopper typed
        href: this.whatsapp + sep + 'text=' + encodeURIComponent(body.slice(0, 400)),
        text: COPY.whatsapp,
        className: 'chat-shopper__cta chat-shopper__cta--wa',
        external: true,
        icon: true
      };
    }

    // Always on, so nobody has to fail a search first to find the human.
    renderFooter() {
      var a = el('a', 'chat-shopper__footer-link', this.waText || COPY.whatsappHelp);
      a.href = this.whatsappLink('').href;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      if (this.waIcon) a.insertBefore(this.waIcon.cloneNode(true), a.firstChild);
      var footer = el('div', 'chat-shopper__footer');
      footer.appendChild(a);
      this.panel.appendChild(footer);
    }

    onSubmit(e) {
      e.preventDefault();
      var text = this.input.value.trim();
      if (!text || !window.ChatShopper) return;

      this.say(text, 'user');
      this.input.value = '';

      var intent = window.ChatShopper.parse(text);
      var named = window.ChatShopper.parseCollectionName(text);
      var blank = window.ChatShopper.isEmpty(intent);

      /*
       * A named collection wins: it is a page the shopper asked for by name.
       * Without the word, the lookup is only a rescue for what would otherwise
       * have been an "I did not understand" reply.
       */
      if (named && (named.explicit || blank)) {
        this.searchCollection(text, intent, named);
        return;
      }
      if (blank) {
        this.say(COPY.empty, 'bot', [this.whatsappLink(text)]);
        return;
      }
      this.searchProducts(text, intent, null);
    }

    searchCollection(text, intent, named) {
      var pending = this.say(COPY.thinking, 'bot', null, true);
      var blank = window.ChatShopper.isEmpty(intent);

      window.ChatShopper.findCollections(named.name).then(function (hits) {
        pending.remove();

        if (hits === null) {
          this.say(COPY.failed, 'bot', [this.whatsappLink(text)]);
          return;
        }
        if (!hits.length) {
          /*
           * The garment words in the same sentence are still usable, so only
           * give up when the collection name was all there was.
           */
          if (blank) {
            this.searchByName(text, named.name);
            return;
          }
          this.say(COPY.noCollection);
          this.searchProducts(text, intent, null);
          return;
        }
        if (hits.length > 1) {
          this.say(COPY.collections, 'bot', hits.map(function (c) {
            return { href: c.url, text: c.title };
          }));
          return;
        }
        // One match, and anything else in the sentence filters inside it.
        if (blank) {
          this.say(COPY.collection(hits[0].title), 'bot', [{ href: hits[0].url, text: COPY.cta }]);
          return;
        }
        this.searchProducts(text, intent, hits[0]);
      }.bind(this));
    }

    /*
     * Nothing in the dictionary and no collection: the words may still be a
     * product name ("mat", "oasis"), which only the store search knows.
     */
    searchByName(text, name) {
      var pending = this.say(COPY.thinking, 'bot', null, true);

      window.ChatShopper.findProducts(name).then(function (found) {
        pending.remove();

        if (found === null) {
          this.say(COPY.failed, 'bot', [this.whatsappLink(text)]);
          return;
        }
        if (found.direct.length) {
          this.say(COPY.products, 'bot', found.direct.map(function (p) {
            return { href: p.url, text: p.title };
          }));
          return;
        }
        if (found.any) {
          var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          var href = root + 'search?type=product&q=' + encodeURIComponent(name);
          this.say(COPY.productsLoose, 'bot', [{ href: href, text: COPY.cta }]);
          return;
        }
        this.say(COPY.empty, 'bot', [this.whatsappLink(text)]);
      }.bind(this));
    }

    searchProducts(text, intent, collection) {
      var pending = this.say(COPY.thinking, 'bot', null, true);
      var base = (collection && collection.url) || this.base || undefined;

      window.ChatShopper.resolve(intent, { base: base }).then(function (res) {
        pending.remove();

        if (res.count === -1) {
          this.say(COPY.failed, 'bot', [this.whatsappLink(text)]);
          return;
        }
        if (res.count === 0) {
          // describe() comma-separates a list; here it has to read as one phrase
          var what = window.ChatShopper.describe(res.intent).replace(COPY.tidy, COPY.tidyInto);
          if (collection) what += COPY.inCollection(collection.title);
          this.say(COPY.nothing(what), 'bot', [this.whatsappLink(text)]);
          return;
        }

        var lines = [COPY.understood + window.ChatShopper.describe(res.intent) +
          (collection ? ',' + COPY.inCollection(collection.title) : '') + '.'];

        if (res.widened) lines.push(COPY.widened);

        var nouns = res.dropped.map(function (k) { return COPY.relaxed[k]; })
          .filter(Boolean);
        if (nouns.length) {
          lines.push(COPY.relaxedPrefix + joinNouns(nouns) + COPY.relaxedSuffix);
        }

        lines.push(res.count >= PAGE_SIZE ? COPY.capped : COPY.results(res.count));

        this.say(lines.join(' '), 'bot', [{ href: res.url, text: COPY.cta }]);
      }.bind(this));
    }
  }

  if (!customElements.get('chat-shopper')) {
    customElements.define('chat-shopper', ChatShopper);
  }
})();
